import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography
} from '@mui/material';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { ROLES } from '../../constants/roles';
import { canApprove } from '../../utils/roleHierarchy';
import AuditTrail from '../AuditTrail';
import ItineraryDownload from '../ItineraryDownload';
import { buildErrorMessage } from './SalesEnquiryUtils';
import { CHARTER_CANCELLATION_REASONS } from '../../constants/charterCancellationReasons';

// Helper functions exported for use in main component
export const canApproveAM = (trip, user, isAccountableManager) => {
  if (!isAccountableManager) return false;
  if (!trip.pendingApprovals) return false;
  return trip.pendingApprovals.some(a => a.role === ROLES.ACCOUNTABLE_MANAGER && a.canApprove?.includes(user?.role));
};

export const canApproveCAMO = (trip, user) => {
  // Only CA_MANAGER, ACCOUNTABLE_MANAGER, and SUPER_ADMIN can approve
  // CA_TECHNICAL_OFFICER (CA Technical Officer) cannot perform ACCEPT/REJECT actions per SCOPE-Role-Structure.md
  if (!canApprove(user?.role)) return false;
  if (user?.role !== ROLES.CA_MANAGER && user?.role !== ROLES.ACCOUNTABLE_MANAGER && user?.role !== ROLES.SUPER_ADMIN) return false;
  if (!trip.pendingApprovals) return false;
  // Show button when CAMO approval is pending (backend will enforce AM approval must come first)
  return trip.pendingApprovals.some(a => a.role === ROLES.CA_MANAGER && a.canApprove?.includes(user?.role));
};

export const canEditTrip = (trip, user) => {
  // CAMO cannot edit trips
  if (user?.role === ROLES.CA_MANAGER) return false;
  if (!trip || !user) return false;
  // Cancelled/grounded charters are not editable
  if (['CANCELLED', 'GROUNDED'].includes(trip.status)) return false;
  // COMPLETED / CHARTER_COMPLETED remain editable for corrections
  // Only SALES, OPERATIONS, ACCOUNTABLE_MANAGER, and SUPER_ADMIN can edit
  const allowedRoles = [ROLES.SALES, ROLES.OPERATIONS_MANAGER, ROLES.OPERATIONS_EXECUTIVE, ROLES.ACCOUNTABLE_MANAGER, ROLES.SUPER_ADMIN];
  return allowedRoles.includes(user?.role);
};

export const canCancelTrip = (trip, user) => {
  if (!trip || !user) return false;
  const allowedRoles = [ROLES.SALES, ROLES.ACCOUNTABLE_MANAGER, ROLES.SUPER_ADMIN];
  if (!allowedRoles.includes(user.role)) return false;
  // COMPLETED (ATC done) and CHARTER_COMPLETED (flight logs done) cannot be cancelled
  return !['CANCELLED', 'COMPLETED', 'CHARTER_COMPLETED', 'GROUNDED'].includes(trip.status);
};

const OtherDialogs = ({
  remarksDialogOpen,
  setRemarksDialogOpen,
  auditTrailOpen,
  setAuditTrailOpen,
  itineraryDialogOpen,
  setItineraryDialogOpen,
  approvalDialogOpen,
  setApprovalDialogOpen,
  cancelDialogOpen,
  setCancelDialogOpen,
  selectedTrip,
  setTrips,
  setSelectedTrip,
  fetchTrips,
  fetchTripDetail,
  selectedTripForRemarks,
  setSelectedTripForRemarks,
  selectedTripForItinerary,
  setSelectedTripForItinerary,
  selectedTripForApproval,
  setSelectedTripForApproval,
  selectedTripForCancel,
  setSelectedTripForCancel,
  auditTrailData,
  setAuditTrailData,
  remarksForm,
  setRemarksForm,
  remarksError,
  setRemarksError,
  sendLoading,
  setSendLoading,
  approvalType,
  setApprovalType,
  approvalForm,
  setApprovalForm,
  approvalLoading,
  setApprovalLoading,
  cancelForm,
  setCancelForm,
  cancelError,
  setCancelError,
  cancelLoading,
  setCancelLoading
}) => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const isAccountableManager = user?.role === ROLES.ACCOUNTABLE_MANAGER || user?.role === ROLES.SUPER_ADMIN;

  const sendToOperations = async () => {
    try {
      const token = localStorage.getItem('token');
      setSendLoading(true);
      
      // Double-check sectors exist before sending (for SALES role)
      if (user?.role === ROLES.SALES) {
        const response = await axios.get(`/api/sales/trips/${selectedTripForRemarks._id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const tripSectors = response.data.sectors || [];
        
        if (tripSectors.length === 0) {
          const errorMsg = 'Cannot send charter to Operations: At least one sector must be created before sending to Operations. Please add sectors to this charter first.';
          setRemarksError(errorMsg);
          showToast(errorMsg, 'error');
          setSendLoading(false);
          return;
        }
      }
      
      // First, add remark if provided
      if (remarksForm.remarks && remarksForm.remarks.trim()) {
        await axios.post(`/api/sales/trips/${selectedTripForRemarks._id}/remarks`, {
          text: remarksForm.remarks.trim()
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      // Then update status
      await axios.put(`/api/sales/trips/${selectedTripForRemarks._id}`, {
        status: 'PENDING_OPS'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchTrips();
      if (selectedTrip?._id === selectedTripForRemarks._id) {
        await fetchTripDetail(selectedTripForRemarks._id);
      }
      setRemarksDialogOpen(false);
      setSelectedTripForRemarks(null);
      setSendLoading(false);
      
      const charterNumber = selectedTripForRemarks.charterNumber || 'N/A';
      showToast(`Charter sent to Operations successfully! Charter: ${charterNumber} | Title: "${selectedTripForRemarks.title}" | Status: PENDING_OPS. Operations team has been notified.`, 'success');
    } catch (error) {
      console.error('Failed to send to operations:', error);
      const errorMsg = error.response?.data?.message || 'Unable to send charter to operations. Please ensure at least one sector exists.';
      setRemarksError(errorMsg);
      showToast(buildErrorMessage('Send to Operations', error), 'error');
      setSendLoading(false);
    }
  };

  const handleApproval = async () => {
    if (!selectedTripForApproval) return;
    
    try {
      setApprovalLoading(true);
      const token = localStorage.getItem('token');
      const endpoint = approvalType === 'AM' 
        ? `/api/sales/trips/${selectedTripForApproval._id}/am-approve`
        : `/api/sales/trips/${selectedTripForApproval._id}/camo-approve`;
      
      await axios.post(endpoint, {
        decision: approvalForm.decision,
        remarks: approvalForm.remarks || undefined
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      await fetchTrips();
      if (selectedTrip?._id === selectedTripForApproval._id) {
        await fetchTripDetail(selectedTripForApproval._id);
      }
      setApprovalDialogOpen(false);
      setSelectedTripForApproval(null);
      setApprovalType(null);
      
      const charterNumber = selectedTripForApproval.charterNumber || 'N/A';
      const approvalRole = approvalType === 'AM' ? 'Accountable Manager' : 'CA Manager';
      const decision = approvalForm.decision === 'APPROVED' ? 'approved' : 'rejected';
      showToast(`${approvalRole} ${decision} successfully! Charter: ${charterNumber} | Title: "${selectedTripForApproval.title}" | Decision: ${approvalForm.decision}`, decision === 'approved' ? 'success' : 'warning');
    } catch (error) {
      console.error('Failed to submit approval:', error);
      showToast(buildErrorMessage(`${approvalType === 'AM' ? 'Accountable Manager' : 'CA Manager'} approval`, error), 'error');
    } finally {
      setApprovalLoading(false);
    }
  };

  const closeCancelDialog = () => {
    setCancelDialogOpen(false);
    setSelectedTripForCancel(null);
    setCancelForm({ cancellationReason: '', description: '' });
    setCancelError(null);
    setCancelLoading(false);
  };

  const handleCancelTrip = async () => {
    if (!selectedTripForCancel) return;
    if (!cancelForm.cancellationReason) {
      setCancelError('Cancellation reason is required');
      showToast('Please select a reason for cancelling this charter.', 'warning');
      return;
    }
    if (!cancelForm.description.trim()) {
      setCancelError('Cancellation description is required');
      showToast('Please provide a description before cancelling this charter.', 'warning');
      return;
    }
    try {
      setCancelLoading(true);
      const token = localStorage.getItem('token');
      await axios.put(`/api/sales/trips/${selectedTripForCancel._id}/cancel`, {
        cancellationReason: cancelForm.cancellationReason,
        description: cancelForm.description.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchTrips();
      if (selectedTrip?._id === selectedTripForCancel._id) {
        await fetchTripDetail(selectedTripForCancel._id);
      }
      closeCancelDialog();
      
      const charterNumber = selectedTripForCancel.charterNumber || 'N/A';
      showToast(`Charter cancelled successfully! Charter: ${charterNumber} | Title: "${selectedTripForCancel.title}" | Reason: ${cancelForm.cancellationReason} | All stakeholders have been notified.`, 'warning');
    } catch (error) {
      console.error('Failed to cancel trip:', error);
      const errMsg = error.response?.data?.message
        || error.response?.data?.errors?.map(e => `${e.path || e.param}: ${e.msg}`).join(', ')
        || 'Unable to cancel charter';
      setCancelError(errMsg);
      showToast(buildErrorMessage('Charter cancellation', error), 'error');
    } finally {
      setCancelLoading(false);
    }
  };

  return (
    <>
      {/* Remarks Dialog */}
      <Dialog open={remarksDialogOpen} onClose={() => setRemarksDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send Charter to Operations</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Charter: {selectedTripForRemarks?.charterNumber} - {selectedTripForRemarks?.title}
          </Typography>
          {remarksError && (
            <Typography variant="body2" color="error" gutterBottom>
              {remarksError}
            </Typography>
          )}
          <TextField
            fullWidth
            label="Remarks (Optional)"
            value={remarksForm.remarks}
            onChange={(e) => setRemarksForm({ remarks: e.target.value })}
            margin="normal"
            multiline
            rows={4}
            placeholder="Add any remarks about sending this charter to operations..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemarksDialogOpen(false)}>Cancel</Button>
          <Button onClick={sendToOperations} variant="contained" disabled={sendLoading}>
            {sendLoading ? 'Sending...' : 'Send to Operations'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Audit Trail Dialog */}
      {auditTrailOpen && auditTrailData && (
        <Dialog
          open={auditTrailOpen}
          onClose={() => setAuditTrailOpen(false)}
          maxWidth="lg"
          fullWidth
          fullScreen
        >
          <AuditTrail
            entityType={auditTrailData.entityType}
            entityId={auditTrailData.entityId}
            entityName={auditTrailData.entityName}
            onClose={() => setAuditTrailOpen(false)}
          />
        </Dialog>
      )}

      {/* Itinerary Download Dialog */}
      {itineraryDialogOpen && selectedTripForItinerary && (
        <ItineraryDownload
          charterNumber={selectedTripForItinerary.charterNumber}
          charterStatus={selectedTripForItinerary.status}
          onClose={() => {
            setItineraryDialogOpen(false);
            setSelectedTripForItinerary(null);
          }}
        />
      )}

      {/* Cancel Trip Dialog */}
      <Dialog open={cancelDialogOpen} onClose={closeCancelDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Cancel Charter</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Charter: {selectedTripForCancel?.charterNumber} — {selectedTripForCancel?.title}
          </Typography>
          <FormControl fullWidth margin="normal" required>
            <InputLabel id="cancel-reason-label">Cancellation Reason</InputLabel>
            <Select
              labelId="cancel-reason-label"
              label="Cancellation Reason"
              value={cancelForm.cancellationReason}
              onChange={(e) => setCancelForm(prev => ({ ...prev, cancellationReason: e.target.value }))}
            >
              {CHARTER_CANCELLATION_REASONS.map((reason) => (
                <MenuItem key={reason} value={reason}>{reason}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Description"
            value={cancelForm.description}
            onChange={(e) => setCancelForm(prev => ({ ...prev, description: e.target.value }))}
            margin="normal"
            required
            multiline
            rows={3}
            helperText="Provide details about why this charter is being cancelled."
          />
          {cancelError && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {cancelError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCancelDialog} disabled={cancelLoading}>Close</Button>
          <Button
            onClick={handleCancelTrip}
            variant="contained"
            color="error"
            disabled={cancelLoading || !cancelForm.cancellationReason || !cancelForm.description.trim()}
          >
            {cancelLoading ? 'Cancelling...' : 'Confirm Cancellation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approval Dialog */}
      <Dialog open={approvalDialogOpen} onClose={() => setApprovalDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {approvalType === 'AM' ? 'Accountable Manager Approval' : 'CA Manager Airworthiness Approval'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Charter: {selectedTripForApproval?.charterNumber} - {selectedTripForApproval?.title}
          </Typography>
          <FormControl fullWidth margin="normal">
            <InputLabel>Decision</InputLabel>
            <Select
              value={approvalForm.decision}
              onChange={(e) => setApprovalForm(prev => ({ ...prev, decision: e.target.value }))}
            >
              <MenuItem value="APPROVED">Approve</MenuItem>
              <MenuItem value="REJECTED">Reject</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Remarks"
            value={approvalForm.remarks}
            onChange={(e) => setApprovalForm(prev => ({ ...prev, remarks: e.target.value }))}
            margin="normal"
            multiline
            rows={3}
            required={approvalForm.decision === 'REJECTED'}
            placeholder={approvalForm.decision === 'REJECTED' ? 'Remarks required when rejecting' : 'Optional remarks...'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApprovalDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleApproval} 
            variant="contained"
            disabled={approvalLoading || (approvalForm.decision === 'REJECTED' && !approvalForm.remarks)}
            color={approvalForm.decision === 'APPROVED' ? 'primary' : 'error'}
          >
            {approvalLoading ? 'Submitting...' : approvalForm.decision === 'APPROVED' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OtherDialogs;
